/**
 * §7.2 screen 5 — Devices (kiosk fleet). Ops data only; no citizen PII.
 *
 * `device.read` is `handler` + `admin`; `device.write` is `admin` alone, so a
 * handler watches the fleet but cannot suspend a kiosk or mint a key.
 *
 * Two things this screen must say out loud:
 *  - **Devices are never deleted.** `murojaats.device_id` references them and the
 *    audit trail outlives the hardware, so retirement is `is_active=false`.
 *  - **The enrollment code is shown once.** It is argon2id-hashed server-side and
 *    is not recoverable; the remedy for a lost code is a new one, and issuing a
 *    new one leaves the live key working until the next enrollment succeeds.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDateTime, formatDuration } from '../lib/format.js';
import { useMahallas } from '../lib/useMahallas.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Field,
  Modal,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';

type DeviceAdmin = components['schemas']['DeviceAdmin'];
type EnrollCodeResponse = components['schemas']['DeviceEnrollCodeResponse'];

/** §4.4 — an item older than 14 days is red in the kiosk's own diagnostics. */
const QUEUE_AGE_ALARM_MS = 14 * 24 * 60 * 60 * 1000;

export function DevicesScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { allows, expire } = useSession();
  const { mahallas } = useMahallas(locale);

  const [rows, setRows] = useState<DeviceAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DeviceAdmin | 'new' | null>(null);
  const [issued, setIssued] = useState<{ device: DeviceAdmin; code: EnrollCodeResponse } | null>(null);

  const canWrite = allows('device.write');

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) return expire();
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `mahalla_name` is the only server-rendered string in this table, and it
      // sits next to a mahalla picker that is already localised through
      // `useMahallas(locale)` — the two disagreeing was the visible defect.
      const data = unwrap(await api().GET('/admin/devices', { params: { query: { locale } } }));
      setRows(data.items);
      setError(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setLoading(false);
    }
  }, [handle, locale]);

  useEffect(() => {
    void load();
  }, [load]);

  async function issueCode(device: DeviceAdmin): Promise<void> {
    try {
      const code = unwrap(
        await api().POST('/admin/devices/{device_id}/enrollment-code', {
          params: { path: { device_id: device.id } },
        }),
      );
      setIssued({ device, code });
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  async function setActive(device: DeviceAdmin, isActive: boolean): Promise<void> {
    if (!isActive && !window.confirm(t('admin.devices.suspendConfirm'))) return;
    try {
      unwrap(
        await api().PATCH('/admin/devices/{device_id}', {
          params: { path: { device_id: device.id } },
          body: { is_active: isActive },
        }),
      );
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  return (
    <Card
      title={t('admin.devices.title')}
      actions={
        canWrite ? <Button onClick={() => setEditing('new')}>{t('admin.devices.addDevice')}</Button> : null
      }
    >
      <ErrorBanner message={error} />
      {loading ? <Spinner label={t('admin.common.loading')} /> : null}
      {!loading && rows.length === 0 ? <EmptyState message={t('admin.devices.empty')} /> : null}

      {rows.length > 0 ? (
        <Table
          head={
            <tr>
              <Th>{t('admin.devices.shortCode')}</Th>
              <Th>{t('admin.devices.label')}</Th>
              <Th>{t('admin.devices.mahalla')}</Th>
              <Th>{t('admin.devices.lastSeen')}</Th>
              <Th>{t('admin.devices.lastHeartbeat')}</Th>
              <Th>{t('admin.devices.queueDepth')}</Th>
              <Th>{t('admin.devices.appVersion')}</Th>
              <Th>{t('admin.common.actions')}</Th>
            </tr>
          }
        >
          {rows.map((device) => {
            const stale =
              device.queue_oldest_ms !== undefined && device.queue_oldest_ms > QUEUE_AGE_ALARM_MS;
            return (
              <Tr key={device.id}>
                <Td className="font-semibold tracking-ref">{device.short_code}</Td>
                <Td>
                  <span className="flex flex-col">
                    {device.label}
                    <span className="flex gap-1 pt-1">
                      {!device.is_active ? (
                        <Badge tone="danger">{t('admin.devices.suspended')}</Badge>
                      ) : null}
                      <Badge tone={device.enrolled ? 'success' : 'gold'}>
                        {device.enrolled
                          ? t('admin.devices.enrolled')
                          : t('admin.devices.notEnrolled')}
                      </Badge>
                    </span>
                  </span>
                </Td>
                <Td>{device.mahalla_name}</Td>
                <Td className="whitespace-nowrap">
                  {device.last_seen_at ? formatDateTime(device.last_seen_at) : t('admin.devices.neverSeen')}
                </Td>
                <Td className="whitespace-nowrap">{formatDateTime(device.last_heartbeat_at)}</Td>
                <Td>
                  {device.queue_depth ? (
                    <span className={stale ? 'font-semibold text-danger-500' : 'text-ink-900'}>
                      {device.queue_depth} · {formatDuration(device.queue_oldest_ms)}
                    </span>
                  ) : (
                    '0'
                  )}
                </Td>
                <Td className="whitespace-nowrap text-xs">
                  {device.app_version ?? '—'}
                  <br />
                  <span className="text-ink-400">{device.os_version ?? ''}</span>
                </Td>
                <Td>
                  {canWrite ? (
                    <span className="flex flex-wrap gap-2">
                      <Button tone="ghost" onClick={() => setEditing(device)}>
                        {t('admin.common.edit')}
                      </Button>
                      <Button tone="ghost" onClick={() => void issueCode(device)}>
                        {t('admin.devices.issueEnrollCode')}
                      </Button>
                      <Button tone="ghost" onClick={() => void setActive(device, !device.is_active)}>
                        {device.is_active ? t('admin.devices.suspend') : t('admin.devices.activate')}
                      </Button>
                    </span>
                  ) : null}
                </Td>
              </Tr>
            );
          })}
        </Table>
      ) : null}

      {rows.some((d) => (d.queue_depth ?? 0) > 0) ? (
        <div className="pt-4">
          <WarningBanner>{t('admin.devices.queueWarning')}</WarningBanner>
        </div>
      ) : null}

      {editing ? (
        <DeviceDialog
          row={editing === 'new' ? null : editing}
          mahallaOptions={mahallas.map((m) => ({ id: m.id, name: m.name }))}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onError={handle}
        />
      ) : null}

      {issued ? (
        <Modal
          title={t('admin.devices.enrollCodeTitle')}
          onClose={() => setIssued(null)}
          footer={
            <Button onClick={() => setIssued(null)}>{t('admin.common.close')}</Button>
          }
        >
          <WarningBanner>{t('admin.devices.enrollCodeOnce')}</WarningBanner>
          <p className="text-sm text-ink-700">
            {issued.device.short_code} · {issued.device.label}
          </p>
          <code className="block rounded-tile bg-surface-100 px-4 py-4 text-center text-2xl font-bold tracking-ref text-brand-900">
            {issued.code.enroll_code}
          </code>
          <p className="text-xs text-ink-500">
            {t('admin.devices.enrollCodeExpires', {
              expires: formatDateTime(issued.code.expires_at),
            })}
          </p>
          <p className="text-xs text-ink-500">{t('admin.devices.enrollCodeHandover')}</p>
        </Modal>
      ) : null}
    </Card>
  );
}

function DeviceDialog({
  row,
  mahallaOptions,
  onClose,
  onSaved,
  onError,
}: {
  row: DeviceAdmin | null;
  mahallaOptions: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [shortCode, setShortCode] = useState(row?.short_code ?? '');
  const [label, setLabel] = useState(row?.label ?? '');
  const [mahallaId, setMahallaId] = useState(row?.mahalla_id ?? mahallaOptions[0]?.id ?? '');
  const [isActive, setIsActive] = useState(row?.is_active ?? true);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      if (row) {
        unwrap(
          await api().PATCH('/admin/devices/{device_id}', {
            params: { path: { device_id: row.id } },
            body: { label, mahalla_id: mahallaId, is_active: isActive },
          }),
        );
      } else {
        unwrap(
          await api().POST('/admin/devices', {
            body: { short_code: shortCode.toUpperCase(), label, mahalla_id: mahallaId },
          }),
        );
      }
      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={row ? t('admin.devices.editDevice') : t('admin.devices.addDevice')}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      {!row ? (
        <Field label={t('admin.devices.shortCode')}>
          <TextInput
            required
            maxLength={3}
            pattern="[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{3}"
            value={shortCode}
            onChange={(event) => setShortCode(event.target.value.toUpperCase())}
          />
        </Field>
      ) : null}

      <Field label={t('admin.devices.label')}>
        <TextInput
          required
          maxLength={120}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </Field>

      <Field label={t('admin.devices.mahalla')}>
        <Select value={mahallaId} onChange={(event) => setMahallaId(event.target.value)}>
          {mahallaOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Select>
      </Field>

      {row ? (
        <Checkbox
          label={t('admin.directory.isActive')}
          checked={isActive}
          onChange={(event) => setIsActive(event.target.checked)}
        />
      ) : null}
    </Modal>
  );
}
